import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Refresh, FilterList, Search, Assignment } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import {
  procurementService,
  ServiceEntryRow,
  ServiceEntryStatus,
  SERVICE_ENTRY_STATUSES,
} from '../../services/procurementService';

const STATUS_TONE: Record<ServiceEntryStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const ServiceEntryPage: React.FC = () => {
  const navigate = useNavigate();

  const [entries, setEntries] = useState<ServiceEntryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<ServiceEntryStatus | ''>('');
  const [search, setSearch] = useState('');

  const fetchEntries = useCallback(() => {
    setLoading(true);
    setError(null);
    procurementService
      .getServiceEntries({ status: statusFilter || undefined })
      .then((r) => setEntries(r.serviceEntries || []))
      .catch((err: any) => setError(err.message || 'Failed to load service entry sheets'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.ses_number?.toLowerCase().includes(q) ||
        e.po_number?.toLowerCase().includes(q) ||
        e.vendor_name?.toLowerCase().includes(q)
    );
  }, [entries, search]);

  const stats = useMemo(() => {
    const draft = entries.filter((e) => e.status === 'DRAFT').length;
    const submitted = entries.filter((e) => e.status === 'SUBMITTED').length;
    const approved = entries.filter((e) => e.status === 'APPROVED').length;
    const totalValue = entries.reduce((sum, e) => sum + toNum(e.total_amount), 0);
    return { draft, submitted, approved, totalValue };
  }, [entries]);

  const hasFilters = !!(statusFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Service Entry Sheets"
        subtitle="Record delivered services against a service purchase order before it can be invoiced"
        actions={
          <>
            <IconButton onClick={fetchEntries} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<Add />}
              size="small"
              onClick={() => navigate('/procurement/service-entries/new')}
            >
              New Service Entry
            </Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Draft" value={stats.draft} icon={<Assignment />} color="#64748b" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Submitted" value={stats.submitted} icon={<Assignment />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Approved" value={stats.approved} icon={<Assignment />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Total Value" value={formatIndianNumber(stats.totalValue)} icon={<Assignment />} color="#7c3aed" />
        </Grid>
      </Grid>

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search SES, PO or vendor"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ServiceEntryStatus | '')}
          sx={{ minWidth: 160 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {SERVICE_ENTRY_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['SES', 'PO', 'Vendor', 'Service Period', 'Status', 'Total'].map((h, i) => (
                  <TableCell key={h} align={i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No service entry sheets match these filters"
                      hint="Clear the filters, or record services delivered against a service PO."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((e) => (
                  <TableRow
                    key={e.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/procurement/service-entries/${e.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={e.ses_number} tint="#7c3aed" icon={<Assignment fontSize="small" />} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {e.po_number}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {e.vendor_name}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(e.service_period_from)} – {fmtDate(e.service_period_to)}
                    </TableCell>
                    <TableCell>
                      <StatusDot label={e.status} tone={STATUS_TONE[e.status] || 'neutral'} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatIndianNumber(toNum(e.total_amount))}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default ServiceEntryPage;
